/**
 * AudienceUserDidNotJoinVideoCallUseCase
 *
 * Ends a call the audience never joined by flipping the `videoCall` message's
 * `isLive` to `false`. Fired by the caller (caller role) when the join-wait
 * window (`AppConstants.videoCallWaitJoinDurationSeconds = 15`) elapses with no
 * audience.
 *
 * Flutter source: lib/presentation/screens/chat/chat_screen.dart:872-889
 *   (`audienceUserDidNotJoinVideoCall`):
 *     - `firebaseUser == null` -> return (`:873-876`).
 *     - locate the message by `videoCallId == messageId` (`:878-880`); on web the
 *       caller supplies the already-resolved entity.
 *     - if found, set `isLive = false`, then persist via
 *       `ChatRepository.saveChatMessage(_e2ee, …)` (`:881-887`).
 *
 * Note there is NO `isLive`-state guard here (unlike `endVideoCallMessage`) —
 * Flutter writes whenever the message exists, so the web port does too.
 *
 * Empty content -> empty-string `''` on the save path (NOT `null`). Flutter
 * writes `content: ''` / `last_content: ''` (chat_repository.dart:20-25
 * else-branch + chat_data_source.dart:35); a `null` `last_content` crashes
 * Flutter's non-nullable parse (chat_message_node.dart:42). The service maps
 * `''` to `last_content: ''`.
 *
 * Pure-domain contract: mutates + persists the supplied entity; no Redux / UI.
 */

import { ChatService } from '@/domain/services/chat/chatService';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import { auth } from '@/data/services/firebase';

export interface AudienceUserDidNotJoinVideoCallParams {
  /** The call session id (`videoCallId`). Carried for parity / logging. */
  callMessageId: string;
  /**
   * The current `videoCall` message entity (the web analog of Flutter's
   * `_listItems.firstWhereOrNull`). `null` -> no-op.
   */
  message: ChatMessageEntity | null;
  /** Chat-users context forwarded to the save path for the metadata doc. */
  chatUsersInfo: ChatUsersInfoEntity;
}

export class AudienceUserDidNotJoinVideoCallUseCase {
  private chatService: ChatService;

  constructor(chatService: ChatService = ChatService.getInstance()) {
    this.chatService = chatService;
  }

  /**
   * Mark the call as ended because the audience never joined.
   *
   * @returns the `ChatService.saveChatMessage` boolean when the write runs;
   *   `false` when there is no authenticated user (`:873-876`) or no message.
   */
  async execute({
    message,
    chatUsersInfo,
  }: AudienceUserDidNotJoinVideoCallParams): Promise<boolean> {
    // Parity `chat_screen.dart:873-876` — no authenticated user -> return.
    if (auth.currentUser == null) {
      return false;
    }

    // Parity `chat_screen.dart:881-887` — write only when the message exists.
    if (message != null) {
      message.isLive = false;
      // Empty content -> empty-string `''` (NOT `null`); the service maps it to
      // `last_content: ''` / `content: ''` (parity chat_data_source.dart:35).
      return this.chatService.saveChatMessage(message, chatUsersInfo, '');
    }

    return false;
  }
}

// Export singleton instance
export const audienceUserDidNotJoinVideoCallUseCase = new AudienceUserDidNotJoinVideoCallUseCase();
